import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import { createElement } from "react";
import { DeleteSweep, Sort } from "@mui/icons-material";
import { useThemeProps } from "@mui/material";
import { forwardRef, useCallback } from "react";
import { useSlotProps } from "../../base/index.js";
import { AddCircle } from "../../icons/index.js";
import { ActionBar, ActionBarItem } from "../ActionBar/index.js";
import { ActionBarContainer } from "./components/ActionBarContainer.js";
import { List } from "./components/List.js";
import { ListItem } from "./components/ListItem.js";
import { Root } from "./components/Root.js";
import { useTexts } from "./hooks/useTexts.js";
import { useUtilityClasses } from "./hooks/useUtilityClasses.js";
import { dynamicListName } from "./classes.js";
import { useStorting } from "./hooks/useStorting.js";
function DynamicListInner(inProps, ref) {
  const themeProps = useThemeProps({
    props: inProps,
    name: dynamicListName
  });
  const {
    sx,
    value,
    testId = "dynamic-list-root",
    errors,
    addItem,
    sorting = "off",
    swapItems,
    className,
    changeItem,
    clearItems,
    deleteItem,
    components,
    RenderItem,
    componentsProps
  } = themeProps;
  const utilityClasses = useUtilityClasses(themeProps);
  const texts = useTexts();
  const { sortingState, showSortingToggle, toggleSorting, toggleSortingLabel } = useStorting(sorting, texts);
  const onClickAddItem = useCallback(() => {
    addItem();
  }, [addItem]);
  const rootSlotProps = useSlotProps({
    ownerState: themeProps,
    elementType: Root,
    className: [className, utilityClasses.root],
    externalSlotProps: componentsProps?.root,
    additionalProps: {
      sx,
      ref,
      as: components?.Root,
      direction: "column",
      "data-testid": testId,
      "data-tidal": "DynamicList"
    }
  });
  const listSlotProps = useSlotProps({
    ownerState: themeProps,
    elementType: List,
    className: utilityClasses.list,
    externalSlotProps: componentsProps?.list,
    additionalProps: {
      as: components?.List,
      spacing: 1,
      direction: "column",
      "data-testid": "dynamic-list-list"
    }
  });
  const listItemSlotProps = useSlotProps({
    ownerState: themeProps,
    elementType: ListItem,
    className: utilityClasses.listItem,
    externalSlotProps: componentsProps?.listItem,
    additionalProps: {
      as: components?.ListItem,
      sorting: sortingState,
      onChange: changeItem,
      onDelete: deleteItem,
      itemsCount: value.length,
      RenderItem,
      onSwapItems: swapItems,
      "data-testid": "dynamic-list-item"
    }
  });
  const actionBarContainerSlotProps = useSlotProps({
    ownerState: themeProps,
    elementType: ActionBarContainer,
    className: utilityClasses.actionBarContainer,
    externalSlotProps: componentsProps?.actionBarContainer,
    additionalProps: {
      as: components?.ActionBarContainer,
      mb: 2,
      direction: "row",
      justifyContent: "flex-end",
      "data-testid": "dynamic-list-action-bar-container"
    }
  });
  return /* @__PURE__ */ jsxs(Root, { ...rootSlotProps, children: [
    /* @__PURE__ */ jsx(ActionBarContainer, { ...actionBarContainerSlotProps, children: /* @__PURE__ */ jsxs(ActionBar, { limit: 2, testId: "dynamic-list-action-bar", children: [
      /* @__PURE__ */ jsx(
        ActionBarItem,
        {
          id: "add",
          icon: /* @__PURE__ */ jsx(AddCircle, {}),
          label: texts.add,
          testId: "dynamic-list-action-bar-add",
          onClick: onClickAddItem,
          tooltip: texts.add
        }
      ),
      /* @__PURE__ */ jsx(
        ActionBarItem,
        {
          id: "clear-all",
          icon: /* @__PURE__ */ jsx(DeleteSweep, {}),
          label: texts.clearAll,
          testId: "dynamic-list-action-bar-clear-all",
          tooltip: texts.clearAll,
          onClick: clearItems
        }
      ),
      showSortingToggle ? /* @__PURE__ */ jsx(
        ActionBarItem,
        {
          id: "toggle-sorting",
          icon: /* @__PURE__ */ jsx(Sort, {}),
          testId: "dynamic-list-action-bar-toggle-sorting",
          label: toggleSortingLabel,
          onClick: toggleSorting
        }
      ) : /* @__PURE__ */ jsx(Fragment, {})
    ] }) }),
    /* @__PURE__ */ jsx(List, { ...listSlotProps, children: value.map((item, index) => /* @__PURE__ */ createElement(
      ListItem,
      {
        ...listItemSlotProps,
        key: `list-item-${index}`,
        index,
        value: item,
        errors: errors?.[index]
      }
    )) })
  ] });
}
const DynamicList = forwardRef(DynamicListInner);
export {
  DynamicList
};
//# sourceMappingURL=DynamicList.js.map
